import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  IconButton,
  Dialog,
  Divider,
  Chip,
  MenuItem,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  ADMIN_LISTING_INTROS_QUERY,
  ADMIN_UPSERT_PAGE_INTRO_MUTATION,
  ADMIN_DELETE_PAGE_INTRO_MUTATION,
} from "../../graphql/pageIntros";
import RichTextEditor from "../../components/RichTextEditor";

const PRODUCT_TYPES = [
  { value: "ring", label: "Rings" },
  { value: "necklace", label: "Necklaces & Pendants" },
  { value: "ear rings", label: "Earrings" },
  { value: "bracelets", label: "Bracelets" },
  { value: "karas", label: "Karas" },
  { value: "bridal sets", label: "Bridal Sets" },
  { value: "wedding band", label: "Wedding Bands" },
];

const METALS = [
  { value: "", label: "All Metals (category page only)" },
  { value: "gold", label: "Gold" },
  { value: "diamond", label: "Diamond" },
  { value: "diamond polki", label: "Diamond Polki" },
  { value: "platinum", label: "Platinum" },
  { value: "palladium", label: "Palladium" },
];

type IntroRow = {
  id: string;
  productType: string;
  metal?: string | null;
  content: string;
  active: boolean;
};

type IntroForm = {
  id?: string;
  productType: string;
  metal: string;
  content: string;
  active: boolean;
};

const emptyForm: IntroForm = {
  productType: "ring",
  metal: "",
  content: "",
  active: true,
};

export default function ListingIntrosAdminPage() {
  const { data, loading, error, refetch } = useQuery(
    ADMIN_LISTING_INTROS_QUERY,
    { fetchPolicy: "cache-and-network" },
  );

  const [upsert, upsertState] = useMutation(ADMIN_UPSERT_PAGE_INTRO_MUTATION);
  const [deleteIntro, deleteState] = useMutation(
    ADMIN_DELETE_PAGE_INTRO_MUTATION,
  );

  const [openForm, setOpenForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<IntroRow | null>(null);
  const [form, setForm] = useState<IntroForm>(emptyForm);

  const intros: IntroRow[] = data?.adminListingIntros ?? [];

  const openCreate = () => {
    setForm(emptyForm);
    setOpenForm(true);
  };
  const openEdit = (r: IntroRow) => {
    setForm({
      id: r.id,
      productType: r.productType,
      metal: r.metal ?? "",
      content: r.content,
      active: r.active,
    });
    setOpenForm(true);
  };

  const handleSave = async () => {
    const res = await upsert({
      variables: {
        id: form.id || null,
        pageType: "listing",
        productType: form.productType,
        metal: form.metal || null,
        content: form.content,
        active: form.active,
      },
    });
    const errors = res?.data?.adminUpsertPageIntro?.errors ?? [];
    if (errors.length) return;
    setOpenForm(false);
    await refetch();
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    await deleteIntro({ variables: { id: confirmDelete.id } });
    setConfirmDelete(null);
    await refetch();
  };

  const getPageLabel = (r: IntroRow) => {
    const type =
      PRODUCT_TYPES.find((t) => t.value === r.productType)?.label ??
      r.productType;
    const metal = r.metal
      ? ` — ${r.metal.charAt(0).toUpperCase() + r.metal.slice(1)}`
      : " — All Metals";
    return `${type}${metal}`;
  };

  const formErrors: string[] =
    upsertState.data?.adminUpsertPageIntro?.errors ?? [];

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        mb={2}
        spacing={2}
      >
        <Box>
          <Typography variant="h5">Listing Page Intro Content</Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            This is the descriptive text that appears below the product grid on
            category pages. Select product type + metal to target a specific
            page. Leave metal as "All Metals" for the general category page
            (e.g. All Rings).
          </Alert>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openCreate}
        >
          Add Intro
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <Paper>
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 220 }}>Page</TableCell>
                <TableCell>Content Preview</TableCell>
                <TableCell sx={{ width: 80 }}>Status</TableCell>
                <TableCell align="right" sx={{ width: 100 }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {intros.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No listing intro content added yet.
                  </TableCell>
                </TableRow>
              )}
              {intros.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell>
                    <Chip
                      size="small"
                      label={getPageLabel(r)}
                      variant="outlined"
                      sx={{ borderRadius: 1, fontSize: 11 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        maxWidth: 400,
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        fontSize: 13,
                        color: "text.secondary",
                      }}
                    >
                      {r.content.replace(/<[^>]*>/g, "")}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={r.active ? "Active" : "Hidden"}
                      color={r.active ? "success" : "default"}
                      variant="outlined"
                      sx={{ borderRadius: 1 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openEdit(r)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setConfirmDelete(r)}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* FORM DIALOG */}
      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        maxWidth="md"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            {form.id ? "Edit Listing Intro" : "Add Listing Intro"}
          </Typography>
          <Divider sx={{ my: 2 }} />
          {formErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.join(" • ")}
            </Alert>
          )}
          <Stack spacing={2}>
            <Alert severity="info" sx={{ fontSize: 12 }}>
              Select which page this intro appears on. "All Metals" = general
              category page. Selecting a metal = that specific metal+category
              page.
            </Alert>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                select
                label="Product Type"
                value={form.productType}
                onChange={(e) =>
                  setForm((p) => ({ ...p, productType: e.target.value }))
                }
                fullWidth
              >
                {PRODUCT_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Metal"
                value={form.metal}
                onChange={(e) =>
                  setForm((p) => ({ ...p, metal: e.target.value }))
                }
                fullWidth
              >
                {METALS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Status"
                value={form.active ? "active" : "hidden"}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    active: e.target.value === "active",
                  }))
                }
                fullWidth
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="hidden">Hidden</MenuItem>
              </TextField>
            </Stack>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Intro Content
              </Typography>
              <RichTextEditor
                value={form.content}
                onChange={(html) => setForm((p) => ({ ...p, content: html }))}
                placeholder="Write the intro content for this page..."
              />
            </Box>
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button
              onClick={() => setOpenForm(false)}
              disabled={upsertState.loading}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={upsertState.loading || !form.content.trim()}
            >
              {upsertState.loading ? "Saving..." : "Save"}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* DELETE CONFIRM */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
      >
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Delete Intro?
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Delete intro for:{" "}
            <b>{confirmDelete ? getPageLabel(confirmDelete) : ""}</b>
          </Typography>
          <Divider sx={{ my: 2 }} />
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleDelete}
              disabled={deleteState.loading}
            >
              {deleteState.loading ? "Deleting..." : "Delete"}
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
